import React from "react"

import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"

import { FieldLabel } from "@/components/shared/forms/FieldLabel"

import { JsonTextareaField } from "./JsonTextareaField"
import { WEBSOCKET_TOOLTIPS } from "./tooltips"
import { HTTP_METHOD_OPTIONS, type WebSocketFieldProps } from "./types"

export const HttpFirstSetupFields: React.FC<WebSocketFieldProps> = ({
  metadata,
  onMetadataUpdate,
  disabled = false,
}) => {
  return (
    <div className="space-y-4">
      <h4 className="text-sm font-medium text-coval-text">HTTP Setup</h4>

      {/* HTTP Endpoint URL */}
      <div className="space-y-2">
        <FieldLabel
          htmlFor="http-endpoint-url"
          tooltip={WEBSOCKET_TOOLTIPS.httpUrl}
        >
          HTTP Endpoint URL <span className="text-coval-danger-text">*</span>
        </FieldLabel>
        <Input
          id="http-endpoint-url"
          aria-label="HTTP Endpoint URL"
          placeholder="https://api.example.com/call"
          value={metadata.http_url || ""}
          onChange={(e) => onMetadataUpdate({ http_url: e.target.value })}
          disabled={disabled}
        />
        <p className="text-xs text-coval-text-muted">
          The HTTP endpoint to call to initiate the session. Must start with
          https:// or http://.
        </p>
      </div>

      {/* HTTP Method */}
      <div className="space-y-2">
        <FieldLabel
          htmlFor="http-method"
          tooltip={WEBSOCKET_TOOLTIPS.httpMethod}
        >
          HTTP Method
        </FieldLabel>
        <Select
          value={metadata.http_method || "POST"}
          onValueChange={(value) => onMetadataUpdate({ http_method: value })}
          disabled={disabled}
        >
          <SelectTrigger id="http-method" aria-label="HTTP Method">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {HTTP_METHOD_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Request Body (JSON) */}
      <JsonTextareaField
        id="http-request-body"
        label="Request Body (JSON)"
        ariaLabel="HTTP Request Body"
        placeholder={
          '{\n  "assistantId": "your-id",\n  "transport": { "provider": "websocket" }\n}'
        }
        description="JSON body sent with the HTTP request to create the session."
        value={metadata.http_request_body}
        fieldName="http_request_body"
        onMetadataUpdate={onMetadataUpdate}
        disabled={disabled}
        rows={4}
        tooltip={WEBSOCKET_TOOLTIPS.httpRequestBody}
      />

      {/* HTTP Headers (JSON) */}
      <JsonTextareaField
        id="http-headers"
        label="HTTP Headers (JSON)"
        ariaLabel="HTTP Headers"
        placeholder={
          '{\n  "Authorization": "Bearer YOUR_API_KEY",\n  "Content-Type": "application/json"\n}'
        }
        description="Headers for the HTTP setup request as a JSON object."
        value={metadata.http_headers}
        fieldName="http_headers"
        onMetadataUpdate={onMetadataUpdate}
        disabled={disabled}
        rows={4}
        tooltip={WEBSOCKET_TOOLTIPS.httpHeaders}
      />

      {/* WebSocket URL Response Path */}
      <div className="space-y-2">
        <FieldLabel
          htmlFor="ws-url-response-path"
          tooltip={WEBSOCKET_TOOLTIPS.websocketUrlResponsePath}
        >
          WebSocket URL Path in Response{" "}
          <span className="text-coval-danger-text">*</span>
        </FieldLabel>
        <Input
          id="ws-url-response-path"
          aria-label="WebSocket URL Path in Response"
          placeholder="websocketCallUrl"
          value={metadata.websocket_url_response_path || ""}
          onChange={(e) =>
            onMetadataUpdate({
              websocket_url_response_path: e.target.value,
            })
          }
          disabled={disabled}
        />
        <p className="text-xs text-coval-text-muted">
          Dot-notation path to the WebSocket URL in the HTTP response JSON
          (e.g., &quot;data.websocket_url&quot; or
          &quot;websocketCallUrl&quot;).
        </p>
      </div>
    </div>
  )
}
